import { useEffect, useMemo, useRef, useState } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';
import StudentInstructorFeedback from './StudentInstructorFeedback';
import ReceivedPeerReviews from './ReceivedPeerReviews';
import { getEffectiveToday, isOpenEverything } from '../../../utils/systemTime';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

// STRUCTURED_FORM_AUTOSAVE_RELIABILITY_V1

const STRUCTURED_TEXT_AUTOSAVE_DELAY_MS =
  1500;

const STRUCTURED_AUTOSAVE_RETRY_DELAY_MS =
  2500;

function headers(extra = {}) {
  return { apikey: SUPABASE_KEY, Authorization: 'Bearer ' + SUPABASE_KEY, ...extra };
}

async function readJson(response, label) {
  const body = await response.text();
  if (!response.ok) throw new Error(`${label} failed (${response.status}): ${body || response.statusText}`);
  return body ? JSON.parse(body) : null;
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function wordCount(value) {
  const plain = stripHtml(value);
  return plain ? plain.split(/\s+/).filter(Boolean).length : 0;
}

function formatDate(value, includeTime = false) {
  if (!value) return '—';
  const date = new Date(includeTime ? value : `${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
  });
}

function addDaysToIsoDate(value, days) {
  if (!value) return null;

  const match = String(value)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return String(value).slice(0, 10);
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    0,
    0
  );

  date.setDate(
    date.getDate() +
    Math.max(0, Number(days) || 0)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function addSignedDaysToIsoDate(
  value,
  days
) {
  if (
    !value ||
    days === '' ||
    days === null ||
    days === undefined ||
    !Number.isFinite(Number(days))
  ) {
    return null;
  }

  const match = String(value)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return null;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    0,
    0
  );

  date.setDate(
    date.getDate() + Number(days)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1)
      .padStart(2, '0'),
    String(date.getDate())
      .padStart(2, '0')
  ].join('-');
}

function getStructuredInstanceDate(
  instance,
  groupDeadlines,
  groupShared,
  groupNumber,
  field
) {
  if (!instance) return null;

  if (
    groupShared &&
    groupNumber
  ) {
    const override =
      (Array.isArray(groupDeadlines)
        ? groupDeadlines
        : []
      ).find(
        row =>
          String(
            row.course_structured_instance_id
          ) ===
            String(instance.id) &&
          Number(row.group_number) ===
            Number(groupNumber)
      );

    const value =
      override?.[field];

    if (value) {
      return String(value).slice(0, 10);
    }
  }

  return instance?.[field]
    ? String(instance[field]).slice(0, 10)
    : null;
}

function isValidHttpsUrl(value) {
  try {
    return new URL(String(value || '').trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

function responseValue(field, response) {
  if (field.field_type === 'multiple_choice') {
    return Array.isArray(response?.response_json) ? response.response_json : [];
  }

  return response?.response_text || '';
}


function structuredFieldValuesEqual(
  field,
  left,
  right
) {
  if (
    field.field_type ===
    'multiple_choice'
  ) {
    return (
      JSON.stringify(
        Array.isArray(left)
          ? left
          : []
      ) ===
      JSON.stringify(
        Array.isArray(right)
          ? right
          : []
      )
    );
  }

  return (
    String(left || '') ===
    String(right || '')
  );
}

function isMeaningful(field, value) {
  if (field.field_type === 'multiple_choice') return Array.isArray(value) && value.length > 0;
  return stripHtml(value).length > 0;
}

function wordLimitProblem(
  field,
  value
) {
  if (
    ![
      'short_text',
      'long_text'
    ].includes(
      field?.field_type
    )
  ) {
    return null;
  }

  const words =
    wordCount(value);

  const min =
    field.word_guidance_min ===
      null ||
    field.word_guidance_min ===
      undefined ||
    field.word_guidance_min ===
      ''
      ? null
      : Number(
          field.word_guidance_min
        );

  const max =
    field.word_guidance_max ===
      null ||
    field.word_guidance_max ===
      undefined ||
    field.word_guidance_max ===
      ''
      ? null
      : Number(
          field.word_guidance_max
        );


  // Optional unanswered questions
  // do not need to meet a minimum.
  if (
    words === 0 &&
    !field.required
  ) {
    return null;
  }


  if (
    min !== null &&
    Number.isFinite(min) &&
    words < min
  ) {
    return {
      kind: 'minimum',
      words,
      limit: min,
      message:
        `“${field.label}” has ${words} word${words === 1 ? '' : 's'}. The minimum is ${min}.`
    };
  }


  if (
    max !== null &&
    Number.isFinite(max) &&
    words > max
  ) {
    return {
      kind: 'maximum',
      words,
      limit: max,
      message:
        `“${field.label}” has ${words} words. The maximum is ${max}.`
    };
  }


  return null;
}


function wordLimitLabel(
  field
) {
  const min =
    field.word_guidance_min ===
      null ||
    field.word_guidance_min ===
      undefined ||
    field.word_guidance_min ===
      ''
      ? null
      : Number(
          field.word_guidance_min
        );

  const max =
    field.word_guidance_max ===
      null ||
    field.word_guidance_max ===
      undefined ||
    field.word_guidance_max ===
      ''
      ? null
      : Number(
          field.word_guidance_max
        );


  if (
    min !== null &&
    max !== null
  ) {
    return (
      `Limit: ${min}–${max} words`
    );
  }


  if (min !== null) {
    return (
      `Minimum: ${min} words`
    );
  }


  if (max !== null) {
    return (
      `Maximum: ${max} words`
    );
  }


  return '';
}


async function loadForm(courseAssignmentId) {
  const id = encodeURIComponent(courseAssignmentId);
  const [sectionsResponse, fieldsResponse, instancesResponse] = await Promise.all([
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_sections?course_assignment_id=eq.${id}&select=*&order=section_order.asc`, { headers: headers() }),
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_fields?course_assignment_id=eq.${id}&select=*&order=field_order.asc`, { headers: headers() }),
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_instances?course_assignment_id=eq.${id}&select=*&order=instance_order.asc`, { headers: headers() })
  ]);

  const sections = await readJson(sectionsResponse, 'Loading form sections');
  const fields = await readJson(fieldsResponse, 'Loading form questions');
  const instances = await readJson(instancesResponse, 'Loading form instances');
  const instanceIds = (instances || []).map(row => row.id);
  let links = [];

  if (instanceIds.length) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_sections?course_structured_instance_id=in.(${instanceIds.map(encodeURIComponent).join(',')})&select=*`,
      { headers: headers() }
    );
    links = await readJson(response, 'Loading instance sections');
  }

  const fieldRows = Array.isArray(fields) ? fields : [];
  return {
    sections: (sections || []).map(section => ({
      ...section,
      fields: fieldRows
        .filter(field => field.course_section_id === section.id)
        .sort((a, b) => Number(a.field_order || 0) - Number(b.field_order || 0))
    })),
    instances: (instances || []).map(instance => ({
      ...instance,
      section_ids: (links || [])
        .filter(link => link.course_structured_instance_id === instance.id)
        .map(link => link.course_structured_section_id)
    }))
  };
}

async function loadResponses(submissionId) {
  if (!submissionId) return [];
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/structured_form_responses?assignment_submission_id=eq.${encodeURIComponent(submissionId)}&select=*`,
    { headers: headers() }
  );
  return readJson(response, 'Loading saved responses');
}

async function loadAssignmentSubmissions(
  enrolmentId,
  courseAssignmentId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${encodeURIComponent(enrolmentId)}&course_assignment_id=eq.${encodeURIComponent(courseAssignmentId)}&select=*,assignment_feedback(*)`,
    { headers: headers() }
  );

  const rows = await readJson(
    response,
    'Loading Structured Form submissions'
  );

  return Array.isArray(rows) ? rows : [];
}

async function getOrCreateGroupWorkspace(
  enrolmentId,
  courseAssignmentId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/get_or_create_structured_form_group_workspace`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_enrolment_id: enrolmentId,
        p_course_assignment_id:
          courseAssignmentId
      })
    }
  );

  return readJson(
    response,
    'Loading shared Structured Form workspace'
  );
}

async function loadGroupInstanceDeadlines(
  courseAssignmentId,
  groupNumber
) {
  if (!courseAssignmentId || !groupNumber) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?group_number=eq.${Number(groupNumber)}&select=*,course_structured_instances!inner(course_assignment_id)&course_structured_instances.course_assignment_id=eq.${encodeURIComponent(courseAssignmentId)}`,
    { headers: headers() }
  );

  const rows = await readJson(
    response,
    'Loading Structured Form group dates'
  );

  return Array.isArray(rows) ? rows : [];
}

async function loadStructuredScheduleState(
  instanceIds
) {
  const ids = [
    ...new Set(
      (instanceIds || [])
        .filter(Boolean)
        .map(String)
    )
  ];

  if (!ids.length) {
    return {
      items: [],
      selections: [],
      readings: [],
      events: []
    };
  }

  const inFilter =
    '(' +
    ids.map(encodeURIComponent).join(',') +
    ')';

  const [itemsResponse, selectionsResponse] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?course_structured_instance_id=in.${inFilter}&select=*&order=display_order.asc,created_at.asc`,
        { headers: headers() }
      ),
      fetch(
        `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?course_structured_instance_id=in.${inFilter}&select=*`,
        { headers: headers() }
      )
    ]);

  const items =
    await readJson(
      itemsResponse,
      'Loading Structured Form Schedule anchors'
    );

  const selections =
    await readJson(
      selectionsResponse,
      'Loading Structured Form Schedule choices'
    );

  const itemRows =
    Array.isArray(items) ? items : [];

  const readingIds = [
    ...new Set(
      itemRows
        .map(
          row =>
            row.week_reading_assignment_id
        )
        .filter(Boolean)
        .map(String)
    )
  ];

  const eventIds = [
    ...new Set(
      itemRows
        .map(row => row.course_event_id)
        .filter(Boolean)
        .map(String)
    )
  ];

  const readingPromise =
    readingIds.length
      ? fetch(
          `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?id=in.(${readingIds.map(encodeURIComponent).join(',')} )&select=id,course_weeks(id,week_number,class_date),course_readings(id,title,author)`.replace(' )', ')'),
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form Schedule readings'
          )
        )
      : Promise.resolve([]);

  const eventPromise =
    eventIds.length
      ? fetch(
          `${SUPABASE_URL}/rest/v1/course_events?id=in.(${eventIds.map(encodeURIComponent).join(',')} )&select=id,title,event_date,event_time,location,course_weeks(id,week_number,class_date)`.replace(' )', ')'),
          { headers: headers() }
        ).then(response =>
          readJson(
            response,
            'Loading Structured Form Schedule events'
          )
        )
      : Promise.resolve([]);

  const [readings, events] =
    await Promise.all([
      readingPromise,
      eventPromise
    ]);

  return {
    items: itemRows,
    selections:
      Array.isArray(selections)
        ? selections
        : [],
    readings:
      Array.isArray(readings)
        ? readings
        : [],
    events:
      Array.isArray(events)
        ? events
        : []
  };
}

function getStructuredScheduleSource(
  item,
  readings,
  events
) {
  if (!item) {
    return {
      label: 'No anchor selected',
      anchorDate: null
    };
  }

  if (item.item_type === 'reading') {
    const occurrence =
      (readings || []).find(
        row =>
          String(row.id) ===
          String(
            item.week_reading_assignment_id
          )
      );

    const week =
      occurrence?.course_weeks;

    const reading =
      occurrence?.course_readings;

    return {
      label: [
        week?.week_number
          ? `Week ${week.week_number}`
          : null,
        reading?.title || 'Reading',
        reading?.author
          ? `by ${reading.author}`
          : null
      ].filter(Boolean).join(' · '),
      anchorDate:
        week?.class_date || null
    };
  }

  const event =
    (events || []).find(
      row =>
        String(row.id) ===
        String(item.course_event_id)
    );

  const week =
    event?.course_weeks;

  return {
    label: [
      week?.week_number
        ? `Week ${week.week_number}`
        : null,
      event?.title || 'Event',
      event?.event_time
        ? String(
            event.event_time
          ).slice(0, 5)
        : null,
      event?.location || null
    ].filter(Boolean).join(' · '),
    anchorDate:
      event?.event_date ||
      week?.class_date ||
      null
  };
}

async function saveStructuredScheduleSelection({
  existingSelection,
  instanceId,
  scheduleItemId,
  enrolmentId,
  groupNumber,
  selectedByEnrolmentId
}) {
  if (
    existingSelection &&
    !scheduleItemId
  ) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?id=eq.${encodeURIComponent(existingSelection.id)}`,
      {
        method: 'DELETE',
        headers: headers()
      }
    );

    await readJson(
      response,
      'Removing Structured Form Schedule choice'
    );

    return null;
  }

  if (existingSelection) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?id=eq.${encodeURIComponent(existingSelection.id)}`,
      {
        method: 'PATCH',
        headers: headers({
          'Content-Type':
            'application/json',
          Prefer:
            'return=representation'
        }),
        body: JSON.stringify({
          course_structured_instance_schedule_item_id:
            scheduleItemId,
          selected_via: 'student',
          selected_by_enrolment_id:
            selectedByEnrolmentId,
          updated_at:
            new Date().toISOString()
        })
      }
    );

    const rows = await readJson(
      response,
      'Changing Structured Form Schedule choice'
    );

    return rows?.[0] || null;
  }

  if (!scheduleItemId) {
    return null;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type':
          'application/json',
        Prefer:
          'return=representation'
      }),
      body: JSON.stringify({
        course_structured_instance_id:
          instanceId,
        course_structured_instance_schedule_item_id:
          scheduleItemId,
        enrolment_id:
          groupNumber
            ? null
            : enrolmentId,
        group_number:
          groupNumber || null,
        selected_via: 'student',
        selected_by_enrolment_id:
          selectedByEnrolmentId
      })
    }
  );

  const rows = await readJson(
    response,
    'Saving Structured Form Schedule choice'
  );

  return rows?.[0] || null;
}

async function createWorkingSubmission(enrolmentId, courseAssignmentId) {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/assignment_submissions`, {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
    body: JSON.stringify({ enrolment_id: enrolmentId, course_assignment_id: courseAssignmentId, submitted: false })
  });
  const rows = await readJson(response, 'Creating form working copy');
  if (!rows?.[0]?.id) throw new Error('The form working copy was not returned.');
  return rows[0];
}

async function saveResponse({ submissionId, field, value, existing }) {
  const now = new Date().toISOString();
  const multiple = field.field_type === 'multiple_choice';
  const payload = {
    assignment_submission_id: submissionId,
    course_structured_field_id: field.id,
    response_text: multiple ? null : String(value || ''),
    response_json: multiple ? (Array.isArray(value) ? value : []) : null,
    first_completed_at: existing?.first_completed_at || (isMeaningful(field, value) ? now : null),
    last_edited_at: now
  };
  const response = await fetch(`${SUPABASE_URL}/rest/v1/structured_form_responses?on_conflict=assignment_submission_id,course_structured_field_id`, {
    method: 'POST',
    headers: headers({
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=representation'
    }),
    body: JSON.stringify(payload)
  });
  const rows = await readJson(response, 'Autosaving response');
  if (!rows?.[0]) throw new Error('The saved response was not returned.');
  return rows[0];
}

async function saveResponsesBatch({
  submissionId,
  entries
}) {
  if (!entries.length) {
    return [];
  }

  const now =
    new Date().toISOString();

  const payload =
    entries.map(
      ({
        field,
        value,
        existing
      }) => {
        const multiple =
          field.field_type ===
          'multiple_choice';

        return {
          assignment_submission_id:
            submissionId,

          course_structured_field_id:
            field.id,

          response_text:
            multiple
              ? null
              : String(
                  value || ''
                ),

          response_json:
            multiple
              ? (
                  Array.isArray(value)
                    ? value
                    : []
                )
              : null,

          first_completed_at:
            existing
              ?.first_completed_at ||
            (
              isMeaningful(
                field,
                value
              )
                ? now
                : null
            ),

          last_edited_at:
            now
        };
      }
    );

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/structured_form_responses?on_conflict=assignment_submission_id,course_structured_field_id`,
      {
        method:
          'POST',

        headers:
          headers({
            'Content-Type':
              'application/json',

            Prefer:
              'resolution=merge-duplicates,return=representation'
          }),

        body:
          JSON.stringify(
            payload
          )
      }
    );

  const rows =
    await readJson(
      response,
      'Saving Structured Form responses'
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


async function loadStructuredScheduleSelections(
  instanceIds
) {
  const ids = [
    ...new Set(
      (instanceIds || [])
        .filter(Boolean)
        .map(String)
    )
  ];

  if (!ids.length) {
    return [];
  }

  const inFilter =
    '(' +
    ids
      .map(
        encodeURIComponent
      )
      .join(',') +
    ')';

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?course_structured_instance_id=in.${inFilter}&select=*`,
      {
        headers:
          headers()
      }
    );

  const rows =
    await readJson(
      response,
      'Refreshing Structured Form Schedule choices'
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


async function submitInstance({
  enrolmentId,
  assignmentId,
  instanceId,
  disclosure,
  groupShared = false
}) {
  const rpcName = groupShared
    ? 'submit_structured_form_group_instance_with_extension'
    : 'submit_structured_form_instance_with_extension';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${rpcName}`,
    {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      p_enrolment_id: enrolmentId,
      p_course_assignment_id: assignmentId,
      p_instance_id: instanceId,
      p_ai_used: disclosure.aiUsed,
      p_ai_policy_confirmed: disclosure.policyConfirmed,
      p_ai_conversation_url: disclosure.conversationUrl || null
    })
    }
  );
  return readJson(response, 'Submitting form instance');
}

async function submitFeedbackResponse(snapshotId, responseHtml) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_feedback?assignment_submission_id=eq.${encodeURIComponent(snapshotId)}&submitted=eq.true`,
    {
      method: 'PATCH',
      headers: headers({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
      body: JSON.stringify({ student_response: responseHtml })
    }
  );
  const rows = await readJson(response, 'Submitting feedback response');
  if (!rows?.[0]) throw new Error('Instructor feedback was not found.');
  return rows[0];
}

// STRUCTURED_NOT_APPLICABLE_CHOICE_V1

const STRUCTURED_OTHER_TEXT_PREFIX =
  '__HSS_OTHER_TEXT__:';

const STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX =
  '__HSS_NOT_APPLICABLE_TEXT__:';

const STRUCTURED_NOT_APPLICABLE_LABEL =
  'Not Applicable';


function isStructuredOtherOption(
  option
) {
  return (
    String(option || '')
      .trim()
      .toLowerCase() ===
    'other'
  );
}


function isStructuredNotApplicableOption(
  option
) {
  return (
    String(option || '')
      .trim()
      .toLowerCase() ===
    'not applicable'
  );
}


function structuredSingleChoiceParts(
  value
) {
  const raw =
    String(value || '');

  if (
    raw.startsWith(
      STRUCTURED_OTHER_TEXT_PREFIX
    )
  ) {
    return {
      selected:
        'Other',

      otherText:
        raw.slice(
          STRUCTURED_OTHER_TEXT_PREFIX
            .length
        ),

      notApplicableText:
        ''
    };
  }

  if (
    raw.startsWith(
      STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
    )
  ) {
    return {
      selected:
        STRUCTURED_NOT_APPLICABLE_LABEL,

      otherText:
        '',

      notApplicableText:
        raw.slice(
          STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
            .length
        )
    };
  }

  return {
    selected:
      raw,

    otherText:
      '',

    notApplicableText:
      ''
  };
}


function structuredSingleChoiceValue(
  option,
  detail = ''
) {
  const selected =
    String(option || '');

  const text =
    String(detail || '');

  if (
    isStructuredOtherOption(
      selected
    )
  ) {
    return text
      ? STRUCTURED_OTHER_TEXT_PREFIX +
          text
      : 'Other';
  }

  if (
    isStructuredNotApplicableOption(
      selected
    )
  ) {
    return text
      ? STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX +
          text
      : STRUCTURED_NOT_APPLICABLE_LABEL;
  }

  return selected;
}


function structuredMultipleChoiceParts(
  value
) {
  const raw =
    Array.isArray(value)
      ? value.map(
          item =>
            String(item)
        )
      : [];

  const otherDetail =
    raw.find(
      item =>
        item.startsWith(
          STRUCTURED_OTHER_TEXT_PREFIX
        )
    );

  const notApplicableDetail =
    raw.find(
      item =>
        item.startsWith(
          STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
        )
    );

  return {
    selected:
      raw.filter(
        item =>
          !item.startsWith(
            STRUCTURED_OTHER_TEXT_PREFIX
          ) &&
          !item.startsWith(
            STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
          )
      ),

    otherText:
      otherDetail
        ? otherDetail.slice(
            STRUCTURED_OTHER_TEXT_PREFIX
              .length
          )
        : '',

    notApplicableText:
      notApplicableDetail
        ? notApplicableDetail.slice(
            STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
              .length
          )
        : ''
  };
}


function structuredMultipleChoiceValue(
  selected,
  otherText,
  notApplicableText
) {
  const cleanSelected =
    (
      Array.isArray(selected)
        ? selected
        : []
    )
      .map(
        item =>
          String(item)
      )
      .filter(
        item =>
          !item.startsWith(
            STRUCTURED_OTHER_TEXT_PREFIX
          ) &&
          !item.startsWith(
            STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX
          )
      );

  const notApplicableOption =
    cleanSelected.find(
      isStructuredNotApplicableOption
    );

  /*
   * Not Applicable is exclusive in a
   * multiple-choice question.
   */
  if (notApplicableOption) {
    const detail =
      String(
        notApplicableText || ''
      );

    return [
      notApplicableOption,

      ...(
        detail
          ? [
              STRUCTURED_NOT_APPLICABLE_TEXT_PREFIX +
              detail
            ]
          : []
      )
    ];
  }

  const hasOther =
    cleanSelected.some(
      isStructuredOtherOption
    );

  const detail =
    String(
      otherText || ''
    );

  return [
    ...cleanSelected,

    ...(
      hasOther &&
      detail
        ? [
            STRUCTURED_OTHER_TEXT_PREFIX +
            detail
          ]
        : []
    )
  ];
}


function FieldControl({
  field,
  value,
  editable,
  onChange,
  onBlur
}) {
  if (
    field.field_type ===
    'short_text'
  ) {
    return (
      <input
        type="text"
        style={styles.textInput}
        value={value}
        readOnly={!editable}
        onChange={event =>
          onChange(
            event.target.value
          )
        }
        onBlur={onBlur}
      />
    );
  }


  if (
    field.field_type ===
    'long_text'
  ) {
    return (
      <RichTextEditor
        value={value}
        readOnly={!editable}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }


  if (
    field.field_type ===
    'single_choice'
  ) {
    const {
      selected,
      otherText,
      notApplicableText
    } =
      structuredSingleChoiceParts(
        value
      );

    return (
      <div style={styles.choiceList}>
        {(field.options || []).map(
          option => {
            const isOther =
              isStructuredOtherOption(
                option
              );

            const isNotApplicable =
              isStructuredNotApplicableOption(
                option
              );

            const checked =
              isOther
                ? isStructuredOtherOption(
                    selected
                  )
                : isNotApplicable
                  ? isStructuredNotApplicableOption(
                      selected
                    )
                  : selected ===
                    option;

            const explanation =
              isOther
                ? otherText
                : isNotApplicable
                  ? notApplicableText
                  : '';

            return (
              <div
                key={option}
                style={{
                  display:
                    'grid',
                  gap:
                    '6px'
                }}
              >
                <label
                  style={
                    styles.choiceRow
                  }
                >
                  <input
                    type="radio"
                    name={
                      `structured-${field.id}`
                    }
                    checked={
                      checked
                    }
                    disabled={
                      !editable
                    }
                    onChange={() =>
                      onChange(
                        structuredSingleChoiceValue(
                          option,
                          ''
                        ),
                        0
                      )
                    }
                  />

                  <span>
                    {option}
                  </span>
                </label>


                {(isOther ||
                  isNotApplicable) &&
                  checked && (
                  <input
                    type="text"
                    style={{
                      ...styles.textInput,
                      marginLeft:
                        '26px',
                      width:
                        'calc(100% - 26px)'
                    }}
                    value={
                      explanation
                    }
                    readOnly={
                      !editable
                    }
                    onBlur={onBlur}
                    placeholder={
                      editable
                        ? (
                            isNotApplicable
                              ? 'Please explain (optional)'
                              : 'Please specify (optional)'
                          )
                        : ''
                    }
                    onChange={
                      event =>
                        onChange(
                          structuredSingleChoiceValue(
                            option,
                            event
                              .target
                              .value
                          ),
                          STRUCTURED_TEXT_AUTOSAVE_DELAY_MS
                        )
                    }
                  />
                )}
              </div>
            );
          }
        )}
      </div>
    );
  }


  const {
    selected,
    otherText,
    notApplicableText
  } =
    structuredMultipleChoiceParts(
      value
    );


  return (
    <div style={styles.choiceList}>
      {(field.options || []).map(
        option => {
          const checked =
            selected.includes(
              option
            );

          const isOther =
            isStructuredOtherOption(
              option
            );

          const isNotApplicable =
            isStructuredNotApplicableOption(
              option
            );

          return (
            <div
              key={option}
              style={{
                display:
                  'grid',
                gap:
                  '6px'
              }}
            >
              <label
                style={
                  styles.choiceRow
                }
              >
                <input
                  type="checkbox"
                  checked={
                    checked
                  }
                  disabled={
                    !editable
                  }
                  onChange={() => {
                    /*
                     * Not Applicable is exclusive.
                     */
                    if (
                      isNotApplicable
                    ) {
                      onChange(
                        structuredMultipleChoiceValue(
                          checked
                            ? []
                            : [
                                option
                              ],
                          '',
                          ''
                        ),
                        0
                      );

                      return;
                    }

                    const withoutNotApplicable =
                      selected.filter(
                        item =>
                          !isStructuredNotApplicableOption(
                            item
                          )
                      );

                    const nextSelected =
                      checked
                        ? withoutNotApplicable.filter(
                            item =>
                              item !==
                              option
                          )
                        : [
                            ...withoutNotApplicable,
                            option
                          ];

                    onChange(
                      structuredMultipleChoiceValue(
                        nextSelected,

                        isOther &&
                        checked
                          ? ''
                          : otherText,

                        ''
                      ),
                      0
                    );
                  }}
                />

                <span>
                  {option}
                </span>
              </label>


              {isOther &&
                checked && (
                <input
                  type="text"
                  style={{
                    ...styles.textInput,
                    marginLeft:
                      '26px',
                    width:
                      'calc(100% - 26px)'
                  }}
                  value={
                    otherText
                  }
                  readOnly={
                    !editable
                  }
                  onBlur={onBlur}
                  placeholder={
                    editable
                      ? 'Please specify (optional)'
                      : ''
                  }
                  onChange={
                    event =>
                      onChange(
                        structuredMultipleChoiceValue(
                          selected,
                          event
                            .target
                            .value,
                          ''
                        ),
                        STRUCTURED_TEXT_AUTOSAVE_DELAY_MS
                      )
                  }
                />
              )}


              {isNotApplicable &&
                checked && (
                <input
                  type="text"
                  style={{
                    ...styles.textInput,
                    marginLeft:
                      '26px',
                    width:
                      'calc(100% - 26px)'
                  }}
                  value={
                    notApplicableText
                  }
                  readOnly={
                    !editable
                  }
                  onBlur={onBlur}
                  placeholder={
                    editable
                      ? 'Please explain (optional)'
                      : ''
                  }
                  onChange={
                    event =>
                      onChange(
                        structuredMultipleChoiceValue(
                          selected,
                          '',
                          event
                            .target
                            .value
                        ),
                        STRUCTURED_TEXT_AUTOSAVE_DELAY_MS
                      )
                  }
                />
              )}
            </div>
          );
        }
      )}
    </div>
  );
}


function SnapshotFeedback({
  snapshot,
  gradingMode,
  enrolmentId,
  onFeedbackChanged
}) {
  const feedback = Array.isArray(snapshot?.assignment_feedback)
    ? snapshot.assignment_feedback.find(row => row.submitted) || snapshot.assignment_feedback[0]
    : snapshot?.assignment_feedback;
  const [responseText, setResponseText] = useState(feedback?.student_response || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setResponseText(feedback?.student_response || '');
    setError('');
  }, [snapshot?.id, feedback?.student_response]);

  if (!feedback?.submitted) return null;

  async function saveStudentResponse() {
    if (!stripHtml(responseText)) {
      setError('Please write a response.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await submitFeedbackResponse(snapshot.id, responseText.trim());
      onFeedbackChanged?.(snapshot.id, saved);
    } catch (err) {
      setError(err?.message || 'Could not submit your response.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.feedbackBox}>
      <StudentInstructorFeedback
        submissionType="assignment"
        submissionId={snapshot?.id}
        enrolmentId={enrolmentId}
        effectiveFeedback={feedback}
        gradingMode={gradingMode}
      />

      {feedback.student_response ? (
        <div style={styles.studentResponse}>
          <div style={styles.feedbackLabel}>Your response</div>
          <div style={styles.richDisplay} dangerouslySetInnerHTML={{ __html: feedback.student_response }} />
        </div>
      ) : (
        <div style={styles.responseForm}>
          <div style={styles.feedbackLabel}>Your response to instructor feedback <span style={styles.optional}>Optional</span></div>
          <RichTextEditor value={responseText} onChange={setResponseText} />
          {error && <div style={styles.error}>{error}</div>}
          <button type="button" style={styles.secondaryBtn} disabled={saving} onClick={saveStudentResponse}>{saving ? 'Submitting...' : 'Submit Response'}</button>
        </div>
      )}
    </div>
  );
}

export default function StudentStructuredForm({
  session,
  assignment,
  enrolment,
  submission,
  snapshotSubmissions = [],
  aiDisclosureEnabled = false,
  permittedAiUses = [],
  gradingMode = 'ungrading',
  scheduleEnabled = false,
  scheduleChoiceEnabled = false,
  scheduleChoiceOpenDate = '',
  scheduleChoiceCloseDate = '',
  groupDeadlinesEnabled = false,
  onSubmissionChanged
}) {
  const [sections, setSections] = useState([]);
  const [instances, setInstances] = useState([]);
  const [responseMap, setResponseMap] = useState({});
  const [workingSubmission, setWorkingSubmission] =
    useState(submission || null);
  const [groupShared, setGroupShared] = useState(false);
  const [groupNumber, setGroupNumber] = useState(null);
  const [
    groupInstanceDeadlines,
    setGroupInstanceDeadlines
  ] = useState([]);
  const [
    structuredScheduleItems,
    setStructuredScheduleItems
  ] = useState([]);
  const [
    structuredScheduleSelections,
    setStructuredScheduleSelections
  ] = useState([]);
  const [
    structuredScheduleReadings,
    setStructuredScheduleReadings
  ] = useState([]);
  const [
    structuredScheduleEvents,
    setStructuredScheduleEvents
  ] = useState([]);
  const [
    scheduleChoiceSavingInstanceId,
    setScheduleChoiceSavingInstanceId
  ] = useState(null);
  const [resolvedEnrolmentId, setResolvedEnrolmentId] =
    useState(enrolment.id);
  const [snapshots, setSnapshots] = useState(snapshotSubmissions);
  const [selectedVersion, setSelectedVersion] = useState('current');
  const [fieldSaveState, setFieldSaveState] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [aiUsed, setAiUsed] = useState(false);
  const [policyConfirmed, setPolicyConfirmed] = useState(false);
  const [conversationUrl, setConversationUrl] = useState('');
  const timersRef =
    useRef({});

  const retryTimersRef =
    useRef({});

  const responseMapRef =
    useRef({});

  const submissionRef =
    useRef(
      submission ||
      null
    );

  const snapshotSubmissionsRef =
    useRef(
      snapshotSubmissions ||
      []
    );

  const creatingRef =
    useRef(null);

  const fieldRevisionRef =
    useRef({});

  const fieldSavedRevisionRef =
    useRef({});

  const fieldRequestedRevisionRef =
    useRef({});

  /*
   * At most one save request may be in flight for a field.
   */
  const fieldSaveQueuesRef =
    useRef({});

  const retryAttemptsRef =
    useRef({});

  const dirtyFieldIdsRef =
    useRef(
      new Set()
    );

  const mountedRef =
    useRef(true);

  const submittingRef =
    useRef(false);


  useEffect(() => {
    mountedRef.current =
      true;

    return () => {
      mountedRef.current =
        false;

      Object.values(
        timersRef.current
      ).forEach(
        timer =>
          window.clearTimeout(
            timer
          )
      );

      Object.values(
        retryTimersRef.current
      ).forEach(
        timer =>
          window.clearTimeout(
            timer
          )
      );

      timersRef.current = {};
      retryTimersRef.current = {};
    };
  }, []);


  /*
   * Protect genuinely unsaved work if the browser/tab is closed or
   * reloaded. This itself makes no Supabase call.
   */
  useEffect(() => {
    function handleBeforeUnload(
      event
    ) {
      const hasUnsavedWork =
        dirtyFieldIdsRef
          .current
          .size > 0 ||
        Object.keys(
          fieldSaveQueuesRef
            .current
        ).length > 0 ||
        !!creatingRef.current ||
        submittingRef.current;

      if (!hasUnsavedWork) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener(
      'beforeunload',
      handleBeforeUnload
    );

    return () => {
      window.removeEventListener(
        'beforeunload',
        handleBeforeUnload
      );
    };
  }, []);

  useEffect(() => {
    if (groupShared) return;

    /*
     * The form may have created its own working copy before the
     * parent's next submission refresh. Do not replace that known
     * working copy with a transient null.
     */
    if (
      !submission &&
      submissionRef.current?.id
    ) {
      return;
    }

    const next =
      submission ||
      null;

    setWorkingSubmission(
      next
    );

    submissionRef.current =
      next;

    setResolvedEnrolmentId(
      enrolment.id
    );
  }, [
    submission,
    enrolment.id,
    groupShared
  ]);

  useEffect(() => {
    snapshotSubmissionsRef.current =
      snapshotSubmissions || [];

    if (!groupShared) {
      setSnapshots(snapshotSubmissions);
    }
  }, [snapshotSubmissions, groupShared]);

  useEffect(() => {
    let cancelled = false;

    async function loadStructuredForm() {
      setLoading(true);
      setError('');

      try {
        let effectiveSubmission =
          submissionRef.current || null;

        let effectiveSnapshots =
          snapshotSubmissionsRef.current || [];

        let effectiveEnrolmentId =
          enrolment.id;

        let shared = false;
        let resolvedGroupNumber = null;
        let resolvedGroupDeadlines = [];

        const usesGroups =
          assignment?.groups_enabled === true &&
          Number(assignment?.group_count) > 0 &&
          session?.isMaster !== true;

        if (usesGroups) {
          const workspace =
            await getOrCreateGroupWorkspace(
              enrolment.id,
              assignment.id
            );

          effectiveEnrolmentId =
            workspace?.owner_enrolment_id;

          resolvedGroupNumber =
            workspace?.group_number ?? null;

          if (!effectiveEnrolmentId) {
            throw new Error(
              'The shared Structured Form workspace did not return an owner enrolment.'
            );
          }

          const sharedSubmissions =
            await loadAssignmentSubmissions(
              effectiveEnrolmentId,
              assignment.id
            );

          effectiveSubmission =
            sharedSubmissions.find(
              row =>
                !row.course_assignment_schedule_item_id &&
                !row.structured_form_instance_id
            ) || null;

          effectiveSnapshots =
            sharedSubmissions.filter(
              row =>
                !!row.structured_form_instance_id &&
                row.submitted === true
            );

          if (
            groupDeadlinesEnabled &&
            !scheduleEnabled
          ) {
            resolvedGroupDeadlines =
              await loadGroupInstanceDeadlines(
                assignment.id,
                resolvedGroupNumber
              );
          }

          shared = true;
        }

        const form =
          await loadForm(
            assignment.id
          );

        const [responses, scheduleState] =
          await Promise.all([
            loadResponses(
              effectiveSubmission?.id
            ),
            scheduleEnabled
              ? loadStructuredScheduleState(
                  form.instances.map(
                    instance => instance.id
                  )
                )
              : Promise.resolve({
                  items: [],
                  selections: [],
                  readings: [],
                  events: []
                })
          ]);

        if (cancelled) return;

        setSections(form.sections);
        setInstances(form.instances);
        setStructuredScheduleItems(
          scheduleState.items
        );
        setStructuredScheduleSelections(
          scheduleState.selections
        );
        setStructuredScheduleReadings(
          scheduleState.readings
        );
        setStructuredScheduleEvents(
          scheduleState.events
        );

        const map = {};

        (responses || []).forEach(row => {
          map[
            row.course_structured_field_id
          ] = row;
        });

        responseMapRef.current =
          map;

        fieldRevisionRef.current =
          {};

        fieldSavedRevisionRef.current =
          {};

        fieldRequestedRevisionRef.current =
          {};

        dirtyFieldIdsRef.current =
          new Set();

        retryAttemptsRef.current =
          {};

        setFieldSaveState(
          {}
        );

        setResponseMap(
          map
        );

        setWorkingSubmission(
          effectiveSubmission
        );

        submissionRef.current =
          effectiveSubmission;

        setResolvedEnrolmentId(
          effectiveEnrolmentId
        );

        setGroupShared(shared);
        setGroupNumber(
          resolvedGroupNumber
        );

        setGroupInstanceDeadlines(
          shared &&
          groupDeadlinesEnabled
            ? resolvedGroupDeadlines
            : []
        );

        if (shared) {
          setSnapshots(
            effectiveSnapshots
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
            'Could not load this Structured Form.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadStructuredForm();

    return () => {
      cancelled = true;

      Object.values(
        timersRef.current
      ).forEach(timer =>
        window.clearTimeout(timer)
      );

      timersRef.current = {};

      Object.values(
        retryTimersRef.current
      ).forEach(
        timer =>
          window.clearTimeout(
            timer
          )
      );

      retryTimersRef.current =
        {};
    };
  }, [
    assignment.id,
    assignment.groups_enabled,
    assignment.group_count,
    enrolment.id,
    session?.isMaster,
    groupDeadlinesEnabled,
    scheduleEnabled
  ]);

  const orderedSnapshots = useMemo(() => [...snapshots]
    .filter(row => row.submitted && row.structured_form_instance_id)
    .sort((a, b) => Number(a.structured_form_snapshot?.instance?.instance_order || 0) - Number(b.structured_form_snapshot?.instance?.instance_order || 0)), [snapshots]);
  const submittedInstanceIds = useMemo(() => new Set(orderedSnapshots.map(row => row.structured_form_instance_id)), [orderedSnapshots]);
  const today = getEffectiveToday(session);
  const openEverything = isOpenEverything(session);

  const assignmentExtensionDays =
    Math.max(
      0,
      Number(
        workingSubmission
          ?.extension_days ??
        assignment
          ?.effective_extension_days ??
        0
      ) || 0
    );

  const scheduleTimingByInstanceId =
    useMemo(() => {
      const result = {};

      for (const instance of instances) {
        const instanceExtensionDays =
          Math.max(
            0,
            Number(
              workingSubmission
                ?.structured_form_instance_extensions
                ?.[String(instance.id)]
            ) || 0
          );

        const extensionDays =
          Math.max(
            assignmentExtensionDays,
            instanceExtensionDays
          );

        if (!scheduleEnabled) {
          const effectiveOpen =
            getStructuredInstanceDate(
              instance,
              groupInstanceDeadlines,
              groupShared,
              groupNumber,
              'open_date'
            );

          const baseClose =
            getStructuredInstanceDate(
              instance,
              groupInstanceDeadlines,
              groupShared,
              groupNumber,
              'close_date'
            );

          result[instance.id] = {
            extensionDays,
            source: null,
            anchorItem: null,
            selection: null,
            openDate:
              effectiveOpen,
            baseCloseDate:
              baseClose,
            closeDate:
              addDaysToIsoDate(
                baseClose,
                extensionDays
              )
          };

          continue;
        }

        const instanceItems =
          structuredScheduleItems.filter(
            item =>
              String(
                item.course_structured_instance_id
              ) === String(instance.id)
          );

        let selection = null;

        if (scheduleChoiceEnabled) {
          selection =
            structuredScheduleSelections.find(
              row => {
                if (
                  String(
                    row.course_structured_instance_id
                  ) !== String(instance.id)
                ) {
                  return false;
                }

                if (groupShared) {
                  return (
                    !row.enrolment_id &&
                    Number(row.group_number) ===
                      Number(groupNumber)
                  );
                }

                return (
                  !row.group_number &&
                  String(row.enrolment_id) ===
                    String(enrolment.id)
                );
              }
            ) || null;
        }

        let anchorItem =
          scheduleChoiceEnabled
            ? instanceItems.find(
                item =>
                  String(item.id) ===
                  String(
                    selection
                      ?.course_structured_instance_schedule_item_id
                  )
              ) || null
            : instanceItems.find(
                item =>
                  item.configuration_mode ===
                  'single'
              ) || null;

        if (
          scheduleChoiceEnabled &&
          !anchorItem &&
          openEverything
        ) {
          anchorItem =
            instanceItems.find(
              item =>
                item.configuration_mode ===
                'choice'
            ) || null;
        }

        const source =
          getStructuredScheduleSource(
            anchorItem,
            structuredScheduleReadings,
            structuredScheduleEvents
          );

        const openDate =
          addSignedDaysToIsoDate(
            source.anchorDate,
            instance.schedule_open_offset_days
          );

        const baseCloseDate =
          addSignedDaysToIsoDate(
            source.anchorDate,
            instance.schedule_close_offset_days
          );

        result[instance.id] = {
          extensionDays,
          source,
          anchorItem,
          selection,
          openDate,
          baseCloseDate,
          closeDate:
            addDaysToIsoDate(
              baseCloseDate,
              extensionDays
            )
        };
      }

      return result;
    }, [
      instances,
      scheduleEnabled,
      scheduleChoiceEnabled,
      structuredScheduleItems,
      structuredScheduleSelections,
      structuredScheduleReadings,
      structuredScheduleEvents,
      groupInstanceDeadlines,
      groupShared,
      groupNumber,
      enrolment.id,
      openEverything,
      assignmentExtensionDays,
      workingSubmission
        ?.structured_form_instance_extensions
    ]);

  const scheduleChoiceWindow =
    useMemo(() => {
      if (!scheduleChoiceEnabled) {
        return {
          editable: false,
          state: 'off',
          message: ''
        };
      }

      if (session?.isMaster) {
        return {
          editable: false,
          state: 'testing',
          message:
            'Testing mode previews the first available Choice option.'
        };
      }

      if (
        scheduleChoiceOpenDate &&
        today < scheduleChoiceOpenDate
      ) {
        return {
          editable: false,
          state: 'not_open',
          message:
            `Choices open ${formatDate(scheduleChoiceOpenDate)}.`
        };
      }

      if (
        scheduleChoiceCloseDate &&
        today > scheduleChoiceCloseDate
      ) {
        return {
          editable: false,
          state: 'closed',
          message:
            `Choices closed ${formatDate(scheduleChoiceCloseDate)}.`
        };
      }

      return {
        editable: true,
        state: 'open',
        message:
          scheduleChoiceCloseDate
            ? `Choices are open until ${formatDate(scheduleChoiceCloseDate)}.`
            : 'Choices are open.'
      };
    }, [
      scheduleChoiceEnabled,
      scheduleChoiceOpenDate,
      scheduleChoiceCloseDate,
      today,
      session?.isMaster
    ]);

  const activeInstance = useMemo(() => {
    const candidates = instances.filter(instance => {
      if (submittedInstanceIds.has(instance.id)) {
        return false;
      }

      if (openEverything) {
        return true;
      }

      const timing =
        scheduleTimingByInstanceId[
          instance.id
        ];

      return !!(
        timing?.openDate &&
        timing?.closeDate &&
        today >= timing.openDate &&
        today <= timing.closeDate
      );
    });

    return candidates
      .sort(
        (a, b) =>
          Number(a.instance_order) -
          Number(b.instance_order)
      )[0] || null;
  }, [
    instances,
    submittedInstanceIds,
    openEverything,
    today,
    scheduleTimingByInstanceId
  ]);

  const nextInstance = useMemo(
    () =>
      instances.find(instance => {
        if (
          submittedInstanceIds.has(instance.id)
        ) {
          return false;
        }

        const timing =
          scheduleTimingByInstanceId[
            instance.id
          ];

        return !!(
          timing?.openDate &&
          timing.openDate > today
        );
      }) || null,
    [
      instances,
      submittedInstanceIds,
      today,
      scheduleTimingByInstanceId
    ]
  );

  const activeTiming =
    activeInstance
      ? scheduleTimingByInstanceId[
          activeInstance.id
        ] || null
      : null;

  const nextTiming =
    nextInstance
      ? scheduleTimingByInstanceId[
          nextInstance.id
        ] || null
      : null;
  const activeSectionIds = useMemo(() => new Set(activeInstance?.section_ids || []), [activeInstance]);
  const selectedSnapshot = selectedVersion === 'current' ? null : orderedSnapshots.find(row => row.id === selectedVersion) || null;
  const displaySections = selectedSnapshot?.structured_form_snapshot?.sections || sections;

  const activeFields = useMemo(() => sections.flatMap(section =>
    activeSectionIds.has(section.id) ? (section.fields || []).map(field => ({ section, field })) : []
  ), [sections, activeSectionIds]);
  const requiredFields = activeFields.filter(({ field }) => field.required);
  const completedRequired = requiredFields.filter(({ field }) => isMeaningful(field, responseValue(field, responseMap[field.id])));

  const wordLimitIssues =
    activeFields
      .map(({ section, field }) => {
        const problem =
          wordLimitProblem(
            field,
            responseValue(
              field,
              responseMap[
                field.id
              ]
            )
          );

        return problem
          ? {
              ...problem,
              section,
              field
            }
          : null;
      })
      .filter(Boolean);

  const canSubmitCurrentInstance =
    completedRequired.length ===
      requiredFields.length &&
    wordLimitIssues.length === 0;

  function getScheduleSelection(
    instanceId
  ) {
    return (
      structuredScheduleSelections.find(
        row => {
          if (
            String(
              row.course_structured_instance_id
            ) !== String(instanceId)
          ) {
            return false;
          }

          if (groupShared) {
            return (
              !row.enrolment_id &&
              Number(row.group_number) ===
                Number(groupNumber)
            );
          }

          return (
            !row.group_number &&
            String(row.enrolment_id) ===
              String(enrolment.id)
          );
        }
      ) || null
    );
  }

  function scheduleItemOccupancy(
    itemId
  ) {
    return structuredScheduleSelections.filter(
      row =>
        String(
          row.course_structured_instance_schedule_item_id
        ) === String(itemId)
    ).length;
  }

  async function handleScheduleChoice(
    instance,
    scheduleItemId
  ) {
    if (
      !scheduleEnabled ||
      !scheduleChoiceEnabled ||
      !scheduleChoiceWindow.editable ||
      submittedInstanceIds.has(
        instance.id
      )
    ) {
      return;
    }

    setScheduleChoiceSavingInstanceId(
      instance.id
    );
    setError('');
    setSuccess('');

    try {
      const existing =
        getScheduleSelection(
          instance.id
        );

      const savedSelection =
        await saveStructuredScheduleSelection({
          existingSelection:
            existing,

          instanceId:
            instance.id,

          scheduleItemId:
            scheduleItemId ||
            null,

          enrolmentId:
            enrolment.id,

          groupNumber:
            groupShared
              ? groupNumber
              : null,

          selectedByEnrolmentId:
            enrolment.id
        });

      /*
       * The POST/PATCH already returned the changed selection.
       * Update that locally rather than reloading immutable schedule
       * items, readings and events.
       */
      setStructuredScheduleSelections(
        previous => {
          const withoutExisting =
            existing?.id
              ? previous.filter(
                  row =>
                    String(
                      row.id
                    ) !==
                    String(
                      existing.id
                    )
                )
              : previous;

          return savedSelection
            ? [
                ...withoutExisting,
                savedSelection
              ]
            : withoutExisting;
        }
      );

      /*
       * If choices have capacity limits, occupancy is shared state.
       * One lightweight selections-only refresh is justified so the
       * student sees current capacity after choosing.
       *
       * Only this instance is refreshed.
       */
      const hasCapacityLimits =
        structuredScheduleItems.some(
          item =>
            String(
              item.course_structured_instance_id
            ) ===
              String(
                instance.id
              ) &&
            item.configuration_mode ===
              'choice' &&
            item.capacity !==
              null &&
            item.capacity !==
              undefined
        );

      if (hasCapacityLimits) {
        const refreshedSelections =
          await loadStructuredScheduleSelections(
            [
              instance.id
            ]
          );

        setStructuredScheduleSelections(
          previous => [
            ...previous.filter(
              row =>
                String(
                  row.course_structured_instance_id
                ) !==
                String(
                  instance.id
                )
            ),

            ...refreshedSelections
          ]
        );
      }

      setSuccess(
        scheduleItemId
          ? `Instance ${instance.instance_order} Schedule choice saved.`
          : `Instance ${instance.instance_order} Schedule choice cleared.`
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not save this Schedule choice.'
      );
    } finally {
      setScheduleChoiceSavingInstanceId(
        null
      );
    }
  }

  async function ensureSubmission() {
    if (
      submissionRef.current?.id
    ) {
      return (
        submissionRef.current
      );
    }

    if (
      creatingRef.current
    ) {
      return (
        creatingRef.current
      );
    }

    creatingRef.current =
      createWorkingSubmission(
        resolvedEnrolmentId,
        assignment.id
      )
        .then(
          created => {
            submissionRef.current =
              created;

            if (
              mountedRef.current
            ) {
              setWorkingSubmission(
                created
              );
            }

            /*
             * Keep the parent's submission list current, but this
             * no longer causes a Structured Form reload.
             */
            onSubmissionChanged?.(
              created
            );

            return created;
          }
        )
        .finally(
          () => {
            creatingRef.current =
              null;
          }
        );

    return creatingRef.current;
  }


  function clearFieldTimer(
    fieldId
  ) {
    const timer =
      timersRef.current[
        fieldId
      ];

    if (!timer) {
      return;
    }

    window.clearTimeout(
      timer
    );

    delete timersRef.current[
      fieldId
    ];
  }


  function clearRetryTimer(
    fieldId
  ) {
    const timer =
      retryTimersRef.current[
        fieldId
      ];

    if (!timer) {
      return;
    }

    window.clearTimeout(
      timer
    );

    delete retryTimersRef.current[
      fieldId
    ];
  }


  function scheduleFieldRetry(
    field
  ) {
    const fieldId =
      String(
        field.id
      );

    if (
      !mountedRef.current ||
      retryTimersRef.current[
        fieldId
      ] ||
      (
        retryAttemptsRef.current[
          fieldId
        ] ||
        0
      ) >= 1
    ) {
      return;
    }

    retryAttemptsRef.current[
      fieldId
    ] =
      (
        retryAttemptsRef.current[
          fieldId
        ] ||
        0
      ) + 1;

    retryTimersRef.current[
      fieldId
    ] =
      window.setTimeout(
        () => {
          delete retryTimersRef
            .current[
              fieldId
            ];

          if (
            !dirtyFieldIdsRef
              .current
              .has(
                fieldId
              )
          ) {
            return;
          }

          requestFieldSave(
            field,
            {
              allowRetry:
                false
            }
          ).catch(
            () => {}
          );
        },

        STRUCTURED_AUTOSAVE_RETRY_DELAY_MS
      );
  }


  function requestFieldSave(
    field,
    {
      allowRetry = true
    } = {}
  ) {
    const fieldId =
      String(
        field.id
      );

    clearFieldTimer(
      fieldId
    );

    const currentRevision =
      fieldRevisionRef.current[
        fieldId
      ] ||
      0;

    if (
      !dirtyFieldIdsRef
        .current
        .has(
          fieldId
        ) &&
      !fieldSaveQueuesRef
        .current[
          fieldId
        ]
    ) {
      return Promise.resolve(
        responseMapRef.current[
          fieldId
        ] ||
        null
      );
    }

    /*
     * Record exactly how far this save request is expected to catch
     * up. If another edit occurs while it is running, that edit gets
     * its own later revision.
     */
    fieldRequestedRevisionRef
      .current[
        fieldId
      ] =
      Math.max(
        fieldRequestedRevisionRef
          .current[
            fieldId
          ] ||
          0,

        currentRevision
      );

    const existingQueue =
      fieldSaveQueuesRef
        .current[
          fieldId
        ];

    if (existingQueue) {
      if (
        mountedRef.current
      ) {
        setFieldSaveState(
          previous => ({
            ...previous,

            [fieldId]:
              'saving'
          })
        );
      }

      return existingQueue;
    }


    let queuePromise;

    queuePromise =
      (async () => {
        while (
          (
            fieldRequestedRevisionRef
              .current[
                fieldId
              ] ||
            0
          ) >
          (
            fieldSavedRevisionRef
              .current[
                fieldId
              ] ||
            0
          )
        ) {
          const revisionAtStart =
            fieldRevisionRef.current[
              fieldId
            ] ||
            0;

          const localAtStart =
            responseMapRef.current[
              fieldId
            ] ||
            {};

          const valueAtStart =
            responseValue(
              field,
              localAtStart
            );

          if (
            mountedRef.current
          ) {
            setFieldSaveState(
              previous => ({
                ...previous,

                [fieldId]:
                  'saving'
              })
            );
          }

          const working =
            await ensureSubmission();

          const saved =
            await saveResponse({
              submissionId:
                working.id,

              field,

              value:
                valueAtStart,

              existing:
                localAtStart
            });

          fieldSavedRevisionRef
            .current[
              fieldId
            ] =
            Math.max(
              fieldSavedRevisionRef
                .current[
                  fieldId
                ] ||
                0,

              revisionAtStart
            );

          retryAttemptsRef.current[
            fieldId
          ] = 0;

          const currentRevisionAfterSave =
            fieldRevisionRef.current[
              fieldId
            ] ||
            0;

          const currentLocal =
            responseMapRef.current[
              fieldId
            ] ||
            {};

          /*
           * The server response may represent an older revision
           * than what is currently in the editor.
           *
           * Preserve useful server metadata but never replace a
           * newer local answer.
           */
          const merged =
            currentRevisionAfterSave ===
            revisionAtStart
              ? saved
              : {
                  ...saved,

                  response_text:
                    field.field_type ===
                    'multiple_choice'
                      ? null
                      : String(
                          currentLocal
                            .response_text ||
                          ''
                        ),

                  response_json:
                    field.field_type ===
                    'multiple_choice'
                      ? (
                          Array.isArray(
                            currentLocal
                              .response_json
                          )
                            ? currentLocal
                                .response_json
                            : []
                        )
                      : null
                };

          responseMapRef.current = {
            ...responseMapRef.current,

            [fieldId]:
              merged
          };

          const savedRevision =
            fieldSavedRevisionRef
              .current[
                fieldId
              ] ||
            0;

          const newestRevision =
            fieldRevisionRef.current[
              fieldId
            ] ||
            0;

          if (
            savedRevision >=
            newestRevision
          ) {
            dirtyFieldIdsRef
              .current
              .delete(
                fieldId
              );
          } else {
            dirtyFieldIdsRef
              .current
              .add(
                fieldId
              );
          }

          if (
            mountedRef.current
          ) {
            setResponseMap(
              responseMapRef.current
            );

            setFieldSaveState(
              previous => ({
                ...previous,

                [fieldId]:
                  savedRevision >=
                  newestRevision
                    ? 'saved'
                    : (
                        (
                          fieldRequestedRevisionRef
                            .current[
                              fieldId
                            ] ||
                          0
                        ) >
                        savedRevision
                          ? 'saving'
                          : 'unsaved'
                      )
              })
            );
          }
        }

        return (
          responseMapRef.current[
            fieldId
          ] ||
          null
        );
      })()
        .catch(
          err => {
            if (
              mountedRef.current
            ) {
              setFieldSaveState(
                previous => ({
                  ...previous,

                  [fieldId]:
                    'error'
                })
              );

              setError(
                err?.message ||
                'A response could not be saved.'
              );
            }

            if (allowRetry) {
              scheduleFieldRetry(
                field
              );
            }

            throw err;
          }
        )
        .finally(
          () => {
            if (
              fieldSaveQueuesRef
                .current[
                  fieldId
                ] ===
              queuePromise
            ) {
              delete fieldSaveQueuesRef
                .current[
                  fieldId
                ];
            }
          }
        );

    fieldSaveQueuesRef.current[
      fieldId
    ] =
      queuePromise;

    return queuePromise;
  }


  function flushField(
    field
  ) {
    const fieldId =
      String(
        field.id
      );

    clearFieldTimer(
      fieldId
    );

    clearRetryTimer(
      fieldId
    );

    if (
      !dirtyFieldIdsRef
        .current
        .has(
          fieldId
        ) &&
      !fieldSaveQueuesRef
        .current[
          fieldId
        ]
    ) {
      return;
    }

    requestFieldSave(
      field
    ).catch(
      () => {}
    );
  }


  function changeValue(
    section,
    field,
    value,
    delay =
      STRUCTURED_TEXT_AUTOSAVE_DELAY_MS
  ) {
    if (
      !activeInstance ||
      !activeSectionIds.has(
        section.id
      ) ||
      selectedVersion !==
        'current' ||
      submittingRef.current
    ) {
      return;
    }

    const fieldId =
      String(
        field.id
      );

    const currentValue =
      responseValue(
        field,
        responseMapRef.current[
          fieldId
        ]
      );

    /*
     * contentEditable can emit equivalent input events. They are
     * not real edits and should not cause Supabase writes.
     */
    if (
      structuredFieldValuesEqual(
        field,
        currentValue,
        value
      )
    ) {
      return;
    }

    const local = {
      ...(
        responseMapRef.current[
          fieldId
        ] ||
        {}
      ),

      course_structured_field_id:
        field.id,

      response_text:
        field.field_type ===
        'multiple_choice'
          ? null
          : String(
              value ||
              ''
            ),

      response_json:
        field.field_type ===
        'multiple_choice'
          ? (
              Array.isArray(value)
                ? value
                : []
            )
          : null
    };

    responseMapRef.current = {
      ...responseMapRef.current,

      [fieldId]:
        local
    };

    fieldRevisionRef.current[
      fieldId
    ] =
      (
        fieldRevisionRef.current[
          fieldId
        ] ||
        0
      ) + 1;

    dirtyFieldIdsRef.current.add(
      fieldId
    );

    retryAttemptsRef.current[
      fieldId
    ] = 0;

    clearRetryTimer(
      fieldId
    );

    if (
      mountedRef.current
    ) {
      setResponseMap(
        responseMapRef.current
      );

      setFieldSaveState(
        previous => ({
          ...previous,

          [fieldId]:
            'unsaved'
        })
      );
    }

    clearFieldTimer(
      fieldId
    );

    /*
     * Radio/checkbox changes currently pass delay 0. Those are
     * discrete changes and are persisted immediately.
     *
     * Text waits for the quiet period, or blur flushes it sooner.
     */
    if (
      Number(delay) <=
      0
    ) {
      requestFieldSave(
        field
      ).catch(
        () => {}
      );

      return;
    }

    timersRef.current[
      fieldId
    ] =
      window.setTimeout(
        () => {
          delete timersRef.current[
            fieldId
          ];

          requestFieldSave(
            field
          ).catch(
            () => {}
          );
        },

        Number(delay) ||
        STRUCTURED_TEXT_AUTOSAVE_DELAY_MS
      );
  }


  async function saveDirtyFieldsForSubmit(
    fieldEntries
  ) {
    const fields =
      fieldEntries.map(
        entry =>
          entry.field
      );

    /*
     * Submit owns the final flush now.
     */
    fields.forEach(
      field => {
        const fieldId =
          String(
            field.id
          );

        clearFieldTimer(
          fieldId
        );

        clearRetryTimer(
          fieldId
        );
      }
    );

    /*
     * Existing requests must finish before the final bulk save.
     * This prevents an older request returning AFTER the final save.
     */
    const inFlight =
      fields
        .map(
          field =>
            fieldSaveQueuesRef
              .current[
                String(
                  field.id
                )
              ]
        )
        .filter(Boolean);

    if (
      inFlight.length
    ) {
      await Promise.allSettled(
        inFlight
      );
    }

    /*
     * A failed request may have scheduled its retry while we were
     * waiting. Submit supersedes that retry.
     */
    fields.forEach(
      field =>
        clearRetryTimer(
          String(
            field.id
          )
        )
    );

    const dirtyEntries =
      fields
        .filter(
          field =>
            dirtyFieldIdsRef
              .current
              .has(
                String(
                  field.id
                )
              )
        )
        .map(
          field => {
            const fieldId =
              String(
                field.id
              );

            return {
              field,

              revision:
                fieldRevisionRef
                  .current[
                    fieldId
                  ] ||
                0,

              value:
                responseValue(
                  field,
                  responseMapRef
                    .current[
                      fieldId
                    ]
                ),

              existing:
                responseMapRef
                  .current[
                    fieldId
                  ] ||
                {}
            };
          }
        );

    /*
     * The snapshot RPC still needs a working-copy submission even
     * when no response currently needs writing.
     */
    const working =
      await ensureSubmission();

    if (
      !dirtyEntries.length
    ) {
      return working;
    }

    if (
      mountedRef.current
    ) {
      setFieldSaveState(
        previous => {
          const next = {
            ...previous
          };

          dirtyEntries.forEach(
            entry => {
              next[
                String(
                  entry.field.id
                )
              ] =
                'saving';
            }
          );

          return next;
        }
      );
    }

    let savedRows;

    try {
      /*
       * Every still-dirty field goes in ONE PostgREST upsert.
       */
      savedRows =
        await saveResponsesBatch({
          submissionId:
            working.id,

          entries:
            dirtyEntries
        });

    } catch (err) {
      if (
        mountedRef.current
      ) {
        setFieldSaveState(
          previous => {
            const next = {
              ...previous
            };

            dirtyEntries.forEach(
              entry => {
                next[
                  String(
                    entry.field.id
                  )
                ] =
                  'error';
              }
            );

            return next;
          }
        );
      }

      throw err;
    }

    const savedByField =
      new Map(
        savedRows.map(
          row => [
            String(
              row.course_structured_field_id
            ),

            row
          ]
        )
      );

    const nextMap = {
      ...responseMapRef.current
    };

    for (
      const entry
      of dirtyEntries
    ) {
      const fieldId =
        String(
          entry.field.id
        );

      const saved =
        savedByField.get(
          fieldId
        );

      if (!saved) {
        throw new Error(
          'A saved Structured Form response was not returned.'
        );
      }

      /*
       * Editing is synchronously blocked with submittingRef, but
       * retain this guard so a future UI change cannot silently
       * snapshot a moving value.
       */
      const currentRevision =
        fieldRevisionRef.current[
          fieldId
        ] ||
        0;

      if (
        currentRevision !==
        entry.revision
      ) {
        throw new Error(
          'A response changed while the form was being submitted. Please submit again.'
        );
      }

      nextMap[
        fieldId
      ] =
        saved;

      fieldSavedRevisionRef.current[
        fieldId
      ] =
        entry.revision;

      fieldRequestedRevisionRef
        .current[
          fieldId
        ] =
        entry.revision;

      retryAttemptsRef.current[
        fieldId
      ] = 0;

      dirtyFieldIdsRef.current.delete(
        fieldId
      );
    }

    responseMapRef.current =
      nextMap;

    if (
      mountedRef.current
    ) {
      setResponseMap(
        nextMap
      );

      setFieldSaveState(
        previous => {
          const next = {
            ...previous
          };

          dirtyEntries.forEach(
            entry => {
              next[
                String(
                  entry.field.id
                )
              ] =
                'saved';
            }
          );

          return next;
        }
      );
    }

    return working;
  }


  async function handleSubmit() {
    setError('');
    setSuccess('');
    if (!activeInstance) return;
    if (completedRequired.length !== requiredFields.length) {
      setError(`Complete all required questions in this instance (${completedRequired.length} of ${requiredFields.length} complete).`);
      return;
    }

    if (wordLimitIssues.length) {
      setError(
        wordLimitIssues[0].message
      );
      return;
    }
    if (aiDisclosureEnabled && aiUsed) {
      if (!permittedAiUses.length) {
        setError('This course does not permit any uses of AI for this assignment.');
        return;
      }
      if (!policyConfirmed) {
        setError('You must confirm that your AI use complied with the course AI Policy.');
        return;
      }
      if (!isValidHttpsUrl(conversationUrl)) {
        setError('Enter a valid HTTPS AI conversation sharing URL.');
        return;
      }
    }

    submittingRef.current =
      true;

    setSubmitting(
      true
    );

    try {
      await saveDirtyFieldsForSubmit(
        activeFields
      );

      const saved =
        await submitInstance({
        enrolmentId: groupShared
          ? enrolment.id
          : resolvedEnrolmentId,
        assignmentId: assignment.id,
        instanceId: activeInstance.id,
        disclosure: {
          aiUsed:
            aiDisclosureEnabled &&
            aiUsed,
          policyConfirmed,
          conversationUrl
        },
        groupShared
      });
      const snapshot = { ...saved, assignment_feedback: null };
      setSnapshots(previous => [...previous.filter(row => row.id !== snapshot.id), snapshot]);
      onSubmissionChanged?.(snapshot);
      setAiUsed(false);
      setPolicyConfirmed(false);
      setConversationUrl('');
      setSuccess(`Instance ${activeInstance.instance_order} submitted. Your working copy is now locked until another instance opens.`);
    } catch (err) {
      setError(err?.message || 'Could not submit this form instance.');
    } finally {
      submittingRef.current =
        false;

      setSubmitting(
        false
      );
    }
  }

  function updateSnapshotFeedback(snapshotId, feedback) {
    setSnapshots(previous => previous.map(row => row.id === snapshotId ? { ...row, assignment_feedback: feedback } : row));
    onSubmissionChanged?.({ ...orderedSnapshots.find(row => row.id === snapshotId), assignment_feedback: feedback });
  }

  if (loading) return <div style={styles.muted}>Loading form...</div>;
  if (!sections.length) return <div style={styles.empty}>This Structured Form has no questions configured.</div>;

  return (
    <div style={styles.wrap}>
      {scheduleEnabled &&
        scheduleChoiceEnabled &&
        selectedVersion === 'current' && (
        <div style={styles.scheduleChoiceCard}>
          <div style={styles.scheduleChoiceTitle}>
            Schedule choices
          </div>

          <div style={styles.scheduleChoiceMessage}>
            {scheduleChoiceWindow.message}
            {groupShared
              ? ' One choice is shared by your Assignment Group for each Instance.'
              : ' Choose the anchor for each Instance.'}
          </div>

          <div style={styles.scheduleChoiceRows}>
            {instances.map(instance => {
              const choices =
                structuredScheduleItems
                  .filter(
                    item =>
                      String(
                        item.course_structured_instance_id
                      ) ===
                        String(instance.id) &&
                      item.configuration_mode ===
                        'choice'
                  )
                  .sort(
                    (a, b) =>
                      Number(
                        a.display_order || 0
                      ) -
                      Number(
                        b.display_order || 0
                      )
                  );

              const selection =
                getScheduleSelection(
                  instance.id
                );

              const selectedItemId =
                selection
                  ?.course_structured_instance_schedule_item_id ||
                '';

              const submitted =
                submittedInstanceIds.has(
                  instance.id
                );

              const disabled =
                submitted ||
                !scheduleChoiceWindow.editable ||
                scheduleChoiceSavingInstanceId ===
                  instance.id;

              return (
                <div
                  key={instance.id}
                  style={styles.scheduleChoiceRow}
                >
                  <div style={styles.scheduleChoiceInstance}>
                    Instance {instance.instance_order}
                    {submitted
                      ? ' · submitted'
                      : ''}
                  </div>

                  <select
                    style={styles.scheduleChoiceSelect}
                    value={selectedItemId}
                    disabled={disabled}
                    onChange={event =>
                      handleScheduleChoice(
                        instance,
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Select an anchor...
                    </option>

                    {choices.map(item => {
                      const source =
                        getStructuredScheduleSource(
                          item,
                          structuredScheduleReadings,
                          structuredScheduleEvents
                        );

                      const occupancy =
                        scheduleItemOccupancy(
                          item.id
                        );

                      const capacity =
                        item.capacity === null ||
                        item.capacity === undefined
                          ? null
                          : Number(
                              item.capacity
                            );

                      const selected =
                        String(
                          selectedItemId
                        ) ===
                        String(item.id);

                      const full =
                        !selected &&
                        capacity !== null &&
                        occupancy >= capacity;

                      return (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={full}
                        >
                          {source.label}
                          {' · '}
                          {capacity === null
                            ? 'Unlimited'
                            : full
                              ? `Full (${occupancy}/${capacity})`
                              : `${occupancy}/${capacity} places`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={styles.versionCard}>
        {groupShared && (
          <div style={styles.groupBanner}>
            Shared Assignment Group
            {groupNumber
              ? ` ${groupNumber}`
              : ''}{' '}
            working copy. Changes made by any
            group member are saved to this same form.
          </div>
        )}

        <label style={styles.versionLabel}>
          Version
          <select style={styles.versionSelect} value={selectedVersion} onChange={event => setSelectedVersion(event.target.value)}>
            <option value="current">Current working version</option>
            {orderedSnapshots.map(snapshot => (
              <option key={snapshot.id} value={snapshot.id}>
                Instance {snapshot.structured_form_snapshot?.instance?.instance_order || '?'} — submitted {formatDate(snapshot.submitted_at, true)}
              </option>
            ))}
          </select>
        </label>
        {selectedSnapshot ? (
          <div style={styles.historyBanner}>Viewing submitted Instance {selectedSnapshot.structured_form_snapshot?.instance?.instance_order} · read-only historical snapshot</div>
        ) : activeInstance ? (
          <div style={styles.currentBanner}>
            <strong>Instance {activeInstance.instance_order} is open</strong>
            {scheduleEnabled && activeTiming?.source?.label
              ? ` · ${activeTiming.source.label}`
              : ''}
            {' · '}
            {formatDate(activeTiming?.openDate)}
            –
            {formatDate(activeTiming?.closeDate)}
            {Number(
              activeTiming?.extensionDays
            ) > 0
              ? ` · ${activeTiming.extensionDays}-day extension`
              : ''}
            {' · '}
            {completedRequired.length} of {requiredFields.length} required questions complete
          </div>
        ) : nextInstance ? (
          <div style={styles.lockedBanner}>
            Working copy locked. Instance {nextInstance.instance_order}
            {scheduleEnabled && nextTiming?.source?.label
              ? ` · ${nextTiming.source.label}`
              : ''}{' '}
            opens {formatDate(nextTiming?.openDate)}.
          </div>
        ) : orderedSnapshots.length === instances.length && instances.length ? (
          <div style={styles.completeBanner}>All instances submitted.</div>
        ) : (
          <div style={styles.lockedBanner}>
            {scheduleEnabled &&
            scheduleChoiceEnabled &&
            instances.some(instance =>
              !submittedInstanceIds.has(instance.id) &&
              !scheduleTimingByInstanceId[instance.id]?.anchorItem
            )
              ? 'No form instance is currently open. One or more future Instances still need a Schedule choice.'
              : 'No form instance is currently open.'}
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {displaySections.map((section, sectionIndex) => {
        const sectionActive = selectedSnapshot ? section.active : activeSectionIds.has(section.id);
        return (
          <section key={section.id} style={{ ...styles.section, ...(!sectionActive ? styles.sectionLocked : {}) }}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionNumber}>Section {sectionIndex + 1}</div>
              <h3 style={styles.sectionTitle}>{section.title}</h3>
              {section.description && <div style={styles.sectionDescription}>{section.description}</div>}
              <div style={sectionActive ? styles.activePill : styles.inactivePill}>
                {selectedSnapshot ? (section.active ? 'Included in this instance' : 'Not open in this instance') : (sectionActive ? 'Open for editing' : 'Read only')}
              </div>
            </div>

            <div style={styles.questionList}>
              {(section.fields || []).map((field, fieldIndex) => {
                const editable =
                  selectedVersion ===
                    'current' &&
                  !!activeInstance &&
                  sectionActive &&
                  !submitting;
                const response = selectedSnapshot ? field : responseMap[field.id];
                const value = responseValue(field, response);
                const words = ['short_text', 'long_text'].includes(field.field_type) ? wordCount(value) : null;
                const saveState = fieldSaveState[field.id];

                const wordProblem =
                  words !== null
                    ? wordLimitProblem(
                        field,
                        value
                      )
                    : null;

                const wordLimit =
                  words !== null
                    ? wordLimitLabel(
                        field
                      )
                    : '';
                return (
                  <div key={field.id} style={{ ...styles.question, ...(!editable ? styles.questionLocked : {}) }}>
                    <div style={styles.questionHeading}>
                      <div>
                        <div style={styles.questionLabel}>{fieldIndex + 1}. {field.label}{field.required && <span style={styles.required}>Required</span>}</div>
                        {field.prompt && <div style={styles.prompt}>{field.prompt}</div>}
                      </div>
                      {editable && saveState && <span style={saveState === 'error' ? styles.saveError : styles.saveState}>{saveState === 'saving' ? 'Saving...' : saveState === 'unsaved' ? 'Unsaved' : saveState === 'error' ? 'Save failed' : 'Saved'}</span>}
                    </div>
                    <FieldControl
                      field={field}
                      value={value}
                      editable={editable}
                      onChange={
                        (next, delay) =>
                          changeValue(
                            section,
                            field,
                            next,
                            delay
                          )
                      }
                      onBlur={
                        () =>
                          flushField(
                            field
                          )
                      }
                    />
                    {words !== null && (
                      <div
                        style={{
                          ...styles.wordLine,
                          ...(wordProblem
                            ? styles.wordError
                            : {})
                        }}
                      >
                        {words} word{words === 1 ? '' : 's'}

                        {wordLimit
                          ? ` · ${wordLimit}`
                          : ''}

                        {wordProblem
                          ? (
                              wordProblem.kind ===
                                'minimum'
                                ? ' · Below minimum'
                                : ' · Above maximum'
                            )
                          : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {selectedVersion === 'current' && activeInstance && aiDisclosureEnabled && (
        <div style={styles.disclosureBox}>
          <div style={styles.disclosureTitle}>AI Disclosure for Instance {activeInstance.instance_order}</div>
          <label style={styles.choiceRow}><input type="radio" name="structured-ai-used" checked={!aiUsed} onChange={() => { setAiUsed(false); setPolicyConfirmed(false); setConversationUrl(''); }} />No AI used</label>
          <label style={styles.choiceRow}><input type="radio" name="structured-ai-used" checked={aiUsed} onChange={() => setAiUsed(true)} />AI used</label>
          {aiUsed && (
            <div style={styles.disclosureDetails}>
              {permittedAiUses.length > 0 && <div style={styles.policyList}><strong>Permitted uses:</strong> {permittedAiUses.map(row => row.ai_use_types?.label).filter(Boolean).join(', ')}</div>}
              <label style={styles.choiceRow}><input type="checkbox" checked={policyConfirmed} onChange={event => setPolicyConfirmed(event.target.checked)} />I confirm that my AI use complied with the course AI Policy.</label>
              <input type="url" style={styles.textInput} value={conversationUrl} onChange={event => setConversationUrl(event.target.value)} placeholder="Enter AI Conversation Sharing URL here" />
            </div>
          )}
        </div>
      )}

      {selectedVersion === 'current' && activeInstance && (
        <button
          type="button"
          style={{
            ...styles.submitBtn,
            ...(
              !canSubmitCurrentInstance
                ? styles.submitDisabled
                : {}
            )
          }}
          disabled={
            submitting ||
            !canSubmitCurrentInstance
          }
          onClick={handleSubmit}
        >
          {submitting ? 'Submitting...' : `Submit Instance ${activeInstance.instance_order}`}
        </button>
      )}

      {selectedSnapshot && (
        <>
          {(typeof selectedSnapshot.ai_used === 'boolean') && (
            <div style={styles.disclosureReadOnly}>
              <strong>AI Disclosure:</strong> Used AI — {selectedSnapshot.ai_used ? 'Yes' : 'No'}
              {selectedSnapshot.ai_conversation_url && <> · <a href={selectedSnapshot.ai_conversation_url} target="_blank" rel="noreferrer">Conversation</a></>}
            </div>
          )}
          <SnapshotFeedback
            snapshot={selectedSnapshot}
            gradingMode={gradingMode}
            enrolmentId={enrolment.id}
            onFeedbackChanged={updateSnapshotFeedback}
          />
          <ReceivedPeerReviews
            targetSubmissionId={selectedSnapshot.id}
          />
        </>
      )}
    </div>
  );
}

const styles = {
  wrap: { display: 'grid', gap: '16px' },
  muted: { color: '#627d98', fontSize: '14px' },
  empty: { padding: '18px', border: '1px dashed #cbd5df', borderRadius: '8px', color: '#627d98' },
  scheduleChoiceCard: {
    display: 'grid',
    gap: '10px',
    padding: '14px 16px',
    border: '1px solid #cbd5df',
    borderRadius: '8px',
    background: '#f7fafc'
  },
  scheduleChoiceTitle: {
    color: '#243b53',
    fontSize: '14px',
    fontWeight: 800
  },
  scheduleChoiceMessage: {
    color: '#526d82',
    fontSize: '12px',
    lineHeight: 1.5
  },
  scheduleChoiceRows: {
    display: 'grid',
    gap: '8px'
  },
  scheduleChoiceRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(110px, 0.45fr) minmax(240px, 1.55fr)',
    gap: '10px',
    alignItems: 'center'
  },
  scheduleChoiceInstance: {
    color: '#486581',
    fontSize: '12px',
    fontWeight: 800
  },
  scheduleChoiceSelect: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '8px 9px',
    background: 'white',
    color: '#243b53'
  },
  versionCard: { display: 'grid', gap: '10px', padding: '14px 16px', border: '1px solid #cbd5df', borderRadius: '8px', background: '#f7fafc' },
  versionLabel: { display: 'grid', gap: '5px', color: '#486581', fontSize: '11px', fontWeight: 800 },
  versionSelect: { border: '1px solid #bcccdc', borderRadius: '6px', padding: '8px 9px', background: 'white', color: '#243b53' },
  currentBanner: { color: '#356b47', fontSize: '12px' },
  groupBanner: {
    padding: '8px 10px',
    borderRadius: '6px',
    background: '#eef6f1',
    color: '#356b47',
    fontSize: '12px',
    fontWeight: 700
  },
  historyBanner: { padding: '8px 10px', borderRadius: '6px', background: '#eef2f6', color: '#526d82', fontSize: '12px' },
  lockedBanner: { color: '#627d98', fontSize: '12px' },
  completeBanner: { color: '#247044', fontSize: '12px', fontWeight: 700 },
  section: { border: '1px solid #d9e2ec', borderRadius: '9px', overflow: 'hidden', background: 'white' },
  sectionLocked: { opacity: 0.82 },
  sectionHeader: { position: 'relative', padding: '15px 16px', background: '#f7fafc', borderBottom: '1px solid #e5ebf0' },
  sectionNumber: { color: '#627d98', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 },
  sectionTitle: { margin: '3px 0 0', color: '#243b53', fontSize: '17px' },
  sectionDescription: { marginTop: '7px', paddingRight: '130px', color: '#526d82', fontSize: '12px', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  activePill: { position: 'absolute', right: '14px', top: '14px', borderRadius: '999px', background: '#e6f4ec', color: '#247044', padding: '4px 8px', fontSize: '9px', fontWeight: 800 },
  inactivePill: { position: 'absolute', right: '14px', top: '14px', borderRadius: '999px', background: '#edf2f7', color: '#627d98', padding: '4px 8px', fontSize: '9px', fontWeight: 800 },
  questionList: { display: 'grid', gap: '1px', background: '#e5ebf0' },
  question: { padding: '16px', background: 'white' },
  questionLocked: { background: '#f8fafb' },
  questionHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', marginBottom: '10px' },
  questionLabel: { color: '#243b53', fontSize: '13px', fontWeight: 700 },
  required: { marginLeft: '7px', borderRadius: '999px', background: '#f1eafa', color: '#67458e', padding: '2px 6px', fontSize: '9px', verticalAlign: 'middle' },
  prompt: { marginTop: '5px', color: '#526d82', fontSize: '12px', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  textInput: { width: '100%', boxSizing: 'border-box', border: '1px solid #bcccdc', borderRadius: '6px', padding: '9px 10px', color: '#243b53', background: 'white' },
  choiceList: { display: 'grid', gap: '8px' },
  choiceRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#334e68', fontSize: '12px', lineHeight: 1.45 },
  wordLine: { marginTop: '7px', color: '#829ab1', fontSize: '10px', textAlign: 'right' },
  wordError: {
    color: '#b42318',
    fontWeight: 700
  },
  saveState: { color: '#52745e', fontSize: '10px', fontWeight: 700 },
  saveError: { color: '#a12a2a', fontSize: '10px', fontWeight: 700 },
  submitBtn: { justifySelf: 'end', border: 'none', borderRadius: '7px', background: '#2f6f9f', color: 'white', padding: '10px 16px', fontWeight: 800, cursor: 'pointer' },
  submitDisabled: { opacity: 0.45, cursor: 'default' },
  disclosureBox: { padding: '14px', border: '1px solid #bcccdc', borderRadius: '8px', background: '#f8fafb', display: 'grid', gap: '9px' },
  disclosureTitle: { color: '#243b53', fontSize: '13px', fontWeight: 800 },
  disclosureDetails: { display: 'grid', gap: '10px', paddingTop: '4px' },
  policyList: { color: '#526d82', fontSize: '11px', lineHeight: 1.5 },
  disclosureReadOnly: { padding: '10px 12px', background: '#f7fafc', border: '1px solid #d9e2ec', borderRadius: '7px', color: '#526d82', fontSize: '12px' },
  feedbackBox: { padding: '14px', background: '#eef6fb', border: '1px solid #c8ddea', borderRadius: '8px', color: '#334e68' },
  feedbackLabel: { color: '#486581', fontSize: '11px', fontWeight: 800, marginBottom: '7px' },
  feedbackMeta: { marginTop: '8px', fontSize: '12px' },
  richDisplay: { fontSize: '13px', lineHeight: 1.55 },
  studentResponse: { marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #c8ddea' },
  responseForm: { display: 'grid', gap: '9px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #c8ddea' },
  optional: { color: '#829ab1', fontWeight: 400 },
  secondaryBtn: { justifySelf: 'start', border: '1px solid #9fb3c8', borderRadius: '6px', background: 'white', color: '#334e68', padding: '7px 11px', fontWeight: 700, cursor: 'pointer' },
  error: { padding: '10px 12px', background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '7px', color: '#a12a2a', fontSize: '12px' },
  success: { padding: '10px 12px', background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '7px', color: '#216e45', fontSize: '12px' }
};
